import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
  HiOutlineAdjustmentsHorizontal,
  HiOutlineArrowPath,
  HiOutlineCheckCircle
} from 'react-icons/hi2';
import SharedModal from '../components/common/SharedModal';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  authService,
  workOrderService,
  machineService,
  plantService,
  lineService,
  masterDataService,
  userService,
  breakdownLogService
} from '../services';
import { SEMANTIC_SUCCESS_MID } from '../theme/chartColors';
import PageLoading from '../components/common/PageLoading';
import './WorkOrder.css';

const initialFormState = {
  workOrderNumber: '',
  workOrderType: 'Breakdown',
  plantId: '',
  lineId: '',
  machineId: '',
  priority: 'Medium',
  title: '',
  description: '',
  requestedBy: 0,
  assignedTo: '',
  scheduledStartTime: '',
  scheduledEndTime: '',
  actualStartTime: '',
  actualEndTime: '',
  statusId: '',
  laborHours: '',
  laborCost: '',
  partsCost: '',
  breakdownLogId: ''
};

const WorkOrder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const breakdownLogId = searchParams.get('breakdownLogId');
  const tenantId = authService.getTenantId();
  const user = authService.getUser();
  const [workOrders, setWorkOrders] = useState([]);
  const [machines, setMachines] = useState([]);
  const [plants, setPlants] = useState([]);
  const [lines, setLines] = useState([]);
  const [users, setUsers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const [filters, setFilters] = useState({
    plantId: '',
    lineId: '',
    machineId: '',
    workOrderType: '',
    statusId: '',
    startDate: '',
    endDate: ''
  });

  const [showModal, setShowModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [statusUpdateOrder, setStatusUpdateOrder] = useState(null);
  const [statusUpdateValue, setStatusUpdateValue] = useState('');

  useEffect(() => {
    if (tenantId) {
      const cachedOrders = sessionStorage.getItem('work_orders_data');
      const cachedMachines = sessionStorage.getItem('machines_data');
      const cachedPlants = sessionStorage.getItem('plants_data');
      const cachedLines = sessionStorage.getItem('lines_data');
      const cachedUsers = sessionStorage.getItem('users_data');
      const cachedStatuses = sessionStorage.getItem('work_order_statuses');

      if (cachedOrders) {
        setWorkOrders(JSON.parse(cachedOrders));
        setLoading(false);
      }
      if (cachedMachines) {
        setMachines(JSON.parse(cachedMachines));
      }
      if (cachedPlants) {
        setPlants(JSON.parse(cachedPlants));
      }
      if (cachedLines) {
        setLines(JSON.parse(cachedLines));
      }
      if (cachedUsers) {
        setUsers(JSON.parse(cachedUsers));
      }
      if (cachedStatuses) {
        setStatuses(JSON.parse(cachedStatuses));
      }

      loadInitialData();

      if (breakdownLogId) {
        handleAddFromBreakdown();
      }
    } else {
      setError('Please login to manage work orders');
      setLoading(false);
    }
  }, [tenantId, breakdownLogId]);

  const loadInitialData = async () => {
    try {
      const hasCachedData = sessionStorage.getItem('work_orders_data');
      if (!hasCachedData) {
        setLoading(true);
      }
      setError('');

      const [ordersData, machinesData, plantsData, linesData, usersData, statusesData] = await Promise.all([
        workOrderService.getWorkOrders(),
        machineService.getAllMachines(),
        plantService.getAllPlants(),
        lineService.getAllLines(),
        userService.getAllUsers(),
        masterDataService.getStatuses(tenantId).catch(() => [])
      ]);

      setWorkOrders(ordersData || []);
      setMachines(machinesData || []);
      setPlants(plantsData || []);
      setLines(linesData || []);
      setUsers(usersData || []);

      // Auto-select plant if only 1 exists
      if (plantsData?.length === 1) {
        setFilters(prev => ({ ...prev, plantId: plantsData[0].plantId.toString() }));
      }
 
      // Filter work orders based on the resolved plant filter
      const resolvedPlantId = plantsData?.length === 1 
        ? plantsData[0].plantId.toString() 
        : '';

      let filteredOrders = ordersData || [];
      if (resolvedPlantId) {
        filteredOrders = filteredOrders.filter(order => {
          const machine = machinesData.find(m => m.machineId === order.machineId);
          if (!machine) return order.plantId?.toString() === resolvedPlantId;
          const line = linesData.find(l => l.lineId === machine.lineId);
          return order.plantId?.toString() === resolvedPlantId || line?.plantId?.toString() === resolvedPlantId;
        });
      }
      setWorkOrders(filteredOrders);

      const actionStatuses = (statusesData || []).filter(
        (s) => s.statusType === 'Action'
      );
      setStatuses(actionStatuses);

      sessionStorage.setItem('work_orders_data', JSON.stringify(ordersData || []));
      sessionStorage.setItem('machines_data', JSON.stringify(machinesData || []));
      sessionStorage.setItem('plants_data', JSON.stringify(plantsData || []));
      sessionStorage.setItem('lines_data', JSON.stringify(linesData || []));
      sessionStorage.setItem('users_data', JSON.stringify(usersData || []));
      sessionStorage.setItem('work_order_statuses', JSON.stringify(actionStatuses));

      if (actionStatuses.length === 0) {
        console.warn('No Action statuses found for work orders');
      }
    } catch (err) {
      console.error(err);
      const msg = 'Failed to load work orders. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFromBreakdown = async () => {
    if (!breakdownLogId) return;

    try {
      const breakdownLog = await breakdownLogService.getBreakdownLogById(parseInt(breakdownLogId, 10));
      if (breakdownLog) {
        const machine = machines.find(m => m.machineId === breakdownLog.machineId);
        const lineId = machine?.lineId || '';
        const plantId = breakdownLog.plantId || (machine?.lineId ? lines.find(l => l.lineId === machine.lineId)?.plantId : '') || '';

        setFormData({
          ...initialFormState,
          workOrderType: 'Breakdown',
          breakdownLogId: breakdownLogId,
          plantId: plantId ? plantId.toString() : '',
          lineId: lineId ? lineId.toString() : '',
          machineId: breakdownLog.machineId || '',
          requestedBy: user?.userId || 0
        });
      } else {
        setFormData({
          ...initialFormState,
          workOrderType: 'Breakdown',
          breakdownLogId: breakdownLogId,
          requestedBy: user?.userId || 0
        });
      }
      setShowModal(true);
    } catch (err) {
      console.error('Error loading breakdown log:', err);
      setFormData({
        ...initialFormState,
        workOrderType: 'Breakdown',
        breakdownLogId: breakdownLogId,
        requestedBy: user?.userId || 0
      });
      setShowModal(true);
    }
  };

  const fetchOrdersWithFilters = async () => {
    try {
      setRefreshing(true);
      setError('');

      const filterParams = {
        machineId: filters.machineId ? parseInt(filters.machineId, 10) : null,
        workOrderType: filters.workOrderType || null,
        statusId: filters.statusId ? parseInt(filters.statusId, 10) : null,
        startDate: filters.startDate ? new Date(filters.startDate) : null,
        endDate: filters.endDate ? new Date(filters.endDate) : null
      };

      let data = await workOrderService.getWorkOrders(filterParams);

      if (filters.plantId || filters.lineId) {
        data = (data || []).filter(order => {
          const machine = machines.find(m => m.machineId === order.machineId);
          if (!machine) return false;

          const line = lines.find(l => l.lineId === machine.lineId);

          if (filters.plantId) {
            const plantMatch = order.plantId?.toString() === filters.plantId ||
              line?.plantId?.toString() === filters.plantId;
            if (!plantMatch) return false;
          }

          if (filters.lineId) {
            if (machine.lineId?.toString() !== filters.lineId) return false;
          }

          return true;
        });
      }

      setWorkOrders(data || []);
    } catch (err) {
      console.error(err);
      const msg = 'Failed to fetch work orders. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setRefreshing(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'plantId') {
        next.lineId = '';
        next.machineId = '';
        if (value) {
            // No automatic default plant setting here to ensure consistency for multi-plant users
        }
      } else if (name === 'lineId') {
        next.machineId = '';
      }
      return next;
    });
  };

  const applyFilters = () => {
    fetchOrdersWithFilters();
    setShowFilterDrawer(false);
  };

  const clearFilters = () => {
    setFilters({
      plantId: '',
      lineId: '',
      machineId: '',
      workOrderType: '',
      statusId: '',
      startDate: '',
      endDate: ''
    });
    // Optional: Clear default plant when filter is cleared? 
    // Usually no, we want to keep it. But if the user says "All Plants", we might clear it.
    // Let's NOT clear localStorage here unless specifically asked.
    loadInitialData();
  };

  const getMachineName = (machineId) => {
    if (!machineId) return '-';
    const m = machines.find((x) => x.machineId === machineId);
    return m ? `${m.machineName} (${m.machineCode})` : `Machine ${machineId}`;
  };

  const getStatusName = (statusId) => {
    if (!statusId) return '-';
    const s = statuses.find((x) => x.statusId === statusId);
    return s ? s.statusName : `Status ${statusId}`;
  };

  const getUserName = (userId) => {
    if (!userId) return '-';
    const u = users.find((x) => x.userId === userId);
    return u ? u.fullName : `User ${userId}`;
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    return d.toLocaleString();
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical':
        return '#ef4444';
      case 'high':
        return '#f59e0b';
      case 'medium':
        return '#3b82f6';
      case 'low':
        return SEMANTIC_SUCCESS_MID;
      default:
        return '#6b7280';
    }
  };

  const handleAdd = () => {
    if (breakdownLogId) {
      navigate(`/home/work-order-wizard?breakdownLogId=${breakdownLogId}`);
    } else {
      navigate('/home/work-order-wizard');
    }
  };

  const handleEdit = (order) => {
    navigate(`/home/work-order-wizard/${order.workOrderId}`);
  };

  const getStepFromStatus = (statusId) => {
    const status = statuses.find(s => s.statusId === statusId);
    if (!status) return 'Step 1';

    const statusName = status.statusName?.toLowerCase() || '';
    if (statusName.includes('draft')) return 'Step 1: Basic Info';
    if (statusName.includes('open')) return 'Step 2-4: Planning';
    if (statusName.includes('progress')) return 'Step 5-7: Execution';
    if (statusName.includes('complete')) return 'Step 8: Costs';
    if (statusName.includes('closed')) return 'Step 9: Completed';
    return 'Step 1';
  };

  const getStepColor = (statusId) => {
    const status = statuses.find(s => s.statusId === statusId);
    if (!status) return '#6b7280';

    const statusName = status.statusName?.toLowerCase() || '';
    if (statusName.includes('draft')) return '#6b7280';
    if (statusName.includes('open')) return '#3b82f6';
    if (statusName.includes('progress')) return '#f59e0b';
    if (statusName.includes('complete')) return SEMANTIC_SUCCESS_MID;
    if (statusName.includes('closed')) return 'var(--accent-primary)';
    return '#6b7280';
  };

  const handleDelete = async (id) => {
    try {
      await workOrderService.deleteWorkOrder(id);
      await fetchOrdersWithFilters();
      setDeleteConfirm(null);
      toast.success('Work order deleted successfully!');
    } catch (err) {
      console.error(err);
      const msg = 'Failed to delete work order.';
      setError(msg);
      toast.error(msg);
    }
  };

  const handleStatusUpdate = async () => {
    if (!statusUpdateOrder || !statusUpdateValue) {
      setError('Please select a status');
      return;
    }

    try {
      const newStatusId = parseInt(statusUpdateValue, 10);
      await workOrderService.updateWorkOrderStatus(statusUpdateOrder.workOrderId, newStatusId);
      await fetchOrdersWithFilters();
      setStatusUpdateOrder(null);
      setStatusUpdateValue('');
      toast.success('Status updated successfully!');
    } catch (err) {
      console.error(err);
      const msg = 'Failed to update work order status.';
      setError(msg);
      toast.error(msg);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'plantId') {
        next.lineId = '';
        next.machineId = '';
      } else if (name === 'lineId') {
        next.machineId = '';
      }
      return next;
    });

    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.machineId) {
      errors.machineId = 'Machine is required';
    }

    if (!formData.title) {
      errors.title = 'Title is required';
    }

    if (!formData.workOrderType) {
      errors.workOrderType = 'Work order type is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const payload = {
        workOrderNumber: formData.workOrderNumber || null,
        workOrderType: formData.workOrderType,
        machineId: parseInt(formData.machineId, 10),
        plantId: formData.plantId ? parseInt(formData.plantId, 10) : null,
        priority: formData.priority,
        title: formData.title,
        description: formData.description || null,
        requestedBy: formData.requestedBy || user?.userId || 0,
        assignedTo: formData.assignedTo ? parseInt(formData.assignedTo, 10) : null,
        scheduledStartTime: formData.scheduledStartTime ? new Date(formData.scheduledStartTime).toISOString() : null,
        scheduledEndTime: formData.scheduledEndTime ? new Date(formData.scheduledEndTime).toISOString() : null,
        actualStartTime: formData.actualStartTime ? new Date(formData.actualStartTime).toISOString() : null,
        actualEndTime: formData.actualEndTime ? new Date(formData.actualEndTime).toISOString() : null,
        statusId: formData.statusId ? parseInt(formData.statusId, 10) : 0,
        laborHours: formData.laborHours ? parseFloat(formData.laborHours) : null,
        laborCost: formData.laborCost ? parseFloat(formData.laborCost) : null,
        partsCost: formData.partsCost ? parseFloat(formData.partsCost) : null,
        breakdownLogId: formData.breakdownLogId ? parseInt(formData.breakdownLogId, 10) : null
      };

      const toastId = toast.loading(editingOrder ? 'Updating work order...' : 'Creating work order...');
      if (editingOrder) {
        await workOrderService.updateWorkOrder(editingOrder.workOrderId, payload);
      } else if (formData.breakdownLogId) {
        await workOrderService.createWorkOrderFromBreakdown(
          parseInt(formData.breakdownLogId, 10),
          payload
        );
      } else {
        await workOrderService.createWorkOrder(payload);
      }

      await fetchOrdersWithFilters();
      setShowModal(false);
      setFormData(initialFormState);
      toast.update(toastId, { render: editingOrder ? 'Work order updated successfully!' : 'Work order created successfully!', type: 'success', isLoading: false, autoClose: 3000, closeButton: true });
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || 'Failed to save work order.';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="pagecontainer">
        <PageLoading message="Loading work orders..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer">
      <div className="page-header">
        <div className="header-content">
          <h1>Work Orders</h1>
          <p>Manage maintenance work orders and track repairs</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleAdd}>
            <HiOutlinePlus />
            New Work Order
          </button>
          <button
            className="btn-secondary"
            onClick={fetchOrdersWithFilters}
            disabled={refreshing}
          >
            <HiOutlineArrowPath className={refreshing ? 'spinning' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container pad10">
        <div className="table-header">
          <button
            className="filter-toggle"
            type="button"
            onClick={() => setShowFilterDrawer(true)}
          >
            <HiOutlineAdjustmentsHorizontal />
            <span>Filters</span>
          </button>
          <div className="table-info">
            {workOrders.length} {workOrders.length === 1 ? 'work order' : 'work orders'}
          </div>
        </div>

        {workOrders.length === 0 ? (
          <div className="empty-state">
            <h3>No work orders found</h3>
            <p>Start by creating your first work order</p>
            <button className="btn-primary" onClick={handleAdd}>
              <HiOutlinePlus />
              Create First Work Order
            </button>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'center' }}>Actions</th>
                <th>WO Number</th>
                <th>Type</th>
                <th>Machine</th>
                <th>Title</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Step</th>
                <th>Assigned To</th>
                <th>Created</th>
                <th>Total Cost</th>
              </tr>
            </thead>
            <tbody>
              {workOrders.map((order) => (
                <tr key={order.workOrderId}>
                  <td style={{ textAlign: 'center' }}>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(order)}
                        title="Edit work order"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-edit"
                        onClick={() => {
                          setStatusUpdateOrder(order);
                          setStatusUpdateValue(order.statusId?.toString() || '');
                        }}
                        title="Update status"
                      >
                        <HiOutlineCheckCircle />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(order)}
                        title="Delete work order"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                  <td>{order.workOrderNumber}</td>
                  <td>
                    <span className="type-badge">{order.workOrderType}</span>
                  </td>
                  <td>{getMachineName(order.machineId)}</td>
                  <td>{order.title}</td>
                  <td>
                    <span
                      className="priority-badge"
                      style={{ backgroundColor: getPriorityColor(order.priority) + '20', color: getPriorityColor(order.priority) }}
                    >
                      {order.priority}
                    </span>
                  </td>
                  <td>
                    <span className="status-pill">
                      {getStatusName(order.statusId)}
                    </span>
                  </td>
                  <td>
                    <span
                      className="step-badge"
                      style={{
                        backgroundColor: getStepColor(order.statusId) + '20',
                        color: getStepColor(order.statusId),
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px', whiteSpace: 'nowrap',
                        fontSize: '0.75rem',
                        fontWeight: '500'
                      }}
                    >
                      {getStepFromStatus(order.statusId)}
                    </span>
                  </td>
                  <td>{getUserName(order.assignedTo)}</td>
                  <td>{formatDateTime(order.createdAt)}</td>
                  <td>
                    {order.totalCost
                      ? `₹${parseFloat(order.totalCost).toFixed(2)}`
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Filter Drawer */}
      {showFilterDrawer && (
        <div
          className="filter-drawer-overlay"
          onClick={() => setShowFilterDrawer(false)}
        >
          <div
            className="filter-drawer"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="filter-drawer-header">
              <h3>Filters</h3>
              <button
                className="btn-icon"
                onClick={() => setShowFilterDrawer(false)}
              >
                <HiOutlineXMark />
              </button>
            </div>
            <div className="filter-drawer-content">
              <div className='row'>

                <div className="form-group col-md-6">
                  <label>Plant</label>
                  <select
                    name="plantId"
                    value={filters.plantId}
                    onChange={handleFilterChange}
                  >
                    <option value="">All Plants</option>
                    {plants.map((p) => (
                      <option key={p.plantId} value={p.plantId}>
                        {p.plantName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group col-md-6">
                  <label>Line</label>
                  <select
                    name="lineId"
                    value={filters.lineId}
                    onChange={handleFilterChange}
                    disabled={!filters.plantId}
                  >
                    <option value="">All Lines</option>
                    {lines
                      .filter(line => !filters.plantId || line.plantId?.toString() === filters.plantId)
                      .map((l) => (
                        <option key={l.lineId} value={l.lineId}>
                          {l.lineName}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="form-group col-md-6">
                  <label>Machine</label>
                  <select
                    name="machineId"
                    value={filters.machineId}
                    onChange={handleFilterChange}
                    disabled={!filters.lineId}
                  >
                    <option value="">All Machines</option>
                    {machines
                      .filter(m => !filters.lineId || m.lineId?.toString() === filters.lineId)
                      .map((m) => (
                        <option key={m.machineId} value={m.machineId}>
                          {m.machineName} ({m.machineCode})
                        </option>
                      ))}
                  </select>
                </div>
                <div className="form-group col-md-6">
                  <label>Work Order Type</label>
                  <select
                    name="workOrderType"
                    value={filters.workOrderType}
                    onChange={handleFilterChange}
                  >
                    <option value="">All Types</option>
                    <option value="Breakdown">Breakdown</option>
                  </select>
                </div>
                <div className="form-group col-md-6">
                  <label>Status</label>
                  <select
                    name="statusId"
                    value={filters.statusId}
                    onChange={handleFilterChange}
                  >
                    <option value="">All Statuses</option>
                    {statuses.map((s) => (
                      <option key={s.statusId} value={s.statusId}>
                        {s.statusName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group col-md-6">
                  <label>Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={filters.startDate}
                    onChange={handleFilterChange}
                  />
                </div>
                <div className="form-group col-md-12">
                  <label>End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    value={filters.endDate}
                    onChange={handleFilterChange}
                  />
                </div>
              </div>
            </div>
            <div className="filter-drawer-footer">
              <button className="btn-secondary btn" onClick={clearFilters}>
                Clear
              </button>
              <button className="btn-primary btn" onClick={applyFilters}>
                Apply
              </button>
            </div>
          </div>

        </div>
      )}

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingOrder ? 'Edit Work Order' : 'New Work Order'}
        customWidth="800px"
      >
        <div className="modal-body">
          <form onSubmit={handleSubmit} className="modal-form">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label required">Work Order Type</label>
                <select
                  name="workOrderType"
                  value={formData.workOrderType}
                  onChange={handleInputChange}
                  className={formErrors.workOrderType ? 'error' : ''}
                >
                  <option value="Breakdown">Breakdown</option>
                </select>
                {formErrors.workOrderType && (
                  <span className="field-error">{formErrors.workOrderType}</span>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Priority</label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Plant</label>
                <select
                  name="plantId"
                  value={formData.plantId}
                  onChange={handleInputChange}
                >
                  <option value="">Select Plant</option>
                  {plants.map((p) => (
                    <option key={p.plantId} value={p.plantId}>
                      {p.plantName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Line</label>
                <select
                  name="lineId"
                  value={formData.lineId}
                  onChange={handleInputChange}
                  disabled={!formData.plantId}
                >
                  <option value="">Select Line</option>
                  {lines
                    .filter(line => !formData.plantId || line.plantId?.toString() === formData.plantId)
                    .map((l) => (
                      <option key={l.lineId} value={l.lineId}>
                        {l.lineName}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label required">Machine</label>
                <select
                  name="machineId"
                  value={formData.machineId}
                  onChange={handleInputChange}
                  className={formErrors.machineId ? 'error' : ''}
                  disabled={!formData.lineId}
                >
                  <option value="">Select Machine</option>
                  {machines
                    .filter(m => !formData.lineId || m.lineId?.toString() === formData.lineId)
                    .map((m) => (
                      <option key={m.machineId} value={m.machineId}>
                        {m.machineName} ({m.machineCode})
                      </option>
                    ))}
                </select>
                {formErrors.machineId && (
                  <span className="field-error">{formErrors.machineId}</span>
                )}
              </div>
              <div className="form-group">
                <label className="form-label required">Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Work order title"
                  className={formErrors.title ? 'error' : ''}
                />
                {formErrors.title && (
                  <span className="field-error">{formErrors.title}</span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="2"
                placeholder="Work order description..."
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Assigned To</label>
                <select
                  name="assignedTo"
                  value={formData.assignedTo}
                  onChange={handleInputChange}
                >
                  <option value="">Unassigned</option>
                  {users.map((u) => (
                    <option key={u.userId} value={u.userId}>
                      {u.fullName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  name="statusId"
                  value={formData.statusId}
                  onChange={handleInputChange}
                >
                  <option value="">Select Status</option>
                  {statuses.map((s) => (
                    <option key={s.statusId} value={s.statusId}>
                      {s.statusName}
                    </option>
                  ))}
                </select>
                {statuses.length === 0 && (
                  <span className="field-error">
                    No statuses available. Please add statuses in Settings.
                  </span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Scheduled Start Time</label>
                <input
                  type="datetime-local"
                  name="scheduledStartTime"
                  value={formData.scheduledStartTime}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Scheduled End Time</label>
                <input
                  type="datetime-local"
                  name="scheduledEndTime"
                  value={formData.scheduledEndTime}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Actual Start Time</label>
                <input
                  type="datetime-local"
                  name="actualStartTime"
                  value={formData.actualStartTime}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Actual End Time</label>
                <input
                  type="datetime-local"
                  name="actualEndTime"
                  value={formData.actualEndTime}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Labor Hours</label>
                <input
                  type="number"
                  name="laborHours"
                  value={formData.laborHours}
                  onChange={handleInputChange}
                  step="0.1"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Labor Cost (₹)</label>
                <input
                  type="number"
                  name="laborCost"
                  value={formData.laborCost}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Parts Cost (₹)</label>
                <input
                  type="number"
                  name="partsCost"
                  value={formData.partsCost}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Breakdown Log ID</label>
                <input
                  type="text"
                  value={formData.breakdownLogId || '-'}
                  disabled
                  className="readonly"
                />
              </div>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : editingOrder ? 'Update Work Order' : 'Create Work Order'}
              </button>
            </div>
          </form>
        </div>
      </SharedModal>

      <SharedModal
        isOpen={!!statusUpdateOrder}
        onClose={() => {
          setStatusUpdateOrder(null);
          setStatusUpdateValue('');
        }}
        title={`Update Status: ${statusUpdateOrder?.workOrderNumber}`}
      >
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Select New Status</label>
            <select
              value={statusUpdateValue}
              onChange={(e) => setStatusUpdateValue(e.target.value)}
            >
              <option value="">Select Status</option>
              {statuses.map((s) => (
                <option key={s.statusId} value={s.statusId}>
                  {s.statusName}
                </option>
              ))}
            </select>
            {statuses.length === 0 && (
              <span className="field-error">
                No statuses available. Please add statuses in Settings.
              </span>
            )}
          </div>
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              setStatusUpdateOrder(null);
              setStatusUpdateValue('');
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleStatusUpdate}
            disabled={!statusUpdateValue}
          >
            Update Status
          </button>
        </div>
      </SharedModal>

      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Work Order"
      >
        <div className="modal-body">
          <p>
            Are you sure you want to delete work order{' '}
            <strong>{deleteConfirm?.workOrderNumber}</strong>?
          </p>
          <p className="warning-text">This action cannot be undone.</p>
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setDeleteConfirm(null)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={() => handleDelete(deleteConfirm?.workOrderId)}
          >
            Delete
          </button>
        </div>
      </SharedModal>
    </div>
  );
};

export default WorkOrder;

